---
title: Modul pohledu zákresu JŽ
sidebar_position: 3
description: Modul pohledu zákresu JŽ
---

# Modul pohledu zákresu JŽ – podrobná analýza

**Modul pohledu zákresu JŽ** (FOI View Module) je primární pracovní prostor pro analýzu zákresů JŽ (Features of Interest). Obsahuje komplexní sadu widgetů a nástrojů pro zkoumání satelitních snímků, signálů, indikátorů a atributů pole.

## Přehled rozvržení pohledu zákresu JŽ

Pohled zákresu JŽ je organizován do tří hlavních sloupců a spodních panelů:

- **Levý sloupec**: Seznam/tabulka zákresů JŽ umožňuje rychlou navigaci mezi zákresy JŽ bez opuštění zobrazení podrobné analýzy.
- **Střední sloupec**: Widgety Pozorování a Mapa pro analýzu snímků
- **Pravý sloupec**: Záložky Atributy zákresu JŽ a Indikátory
- **Spodní panely**: Widgety Vizualizace signálů a indikátorů a Časosběrnice

**Poznámka**: Funkce seznamu zákresů JŽ jsou popsány v části [Widget seznam/tabulka zákresů JŽ](02-getting-started.mdx#widget-seznamutabulky-zákresů-jž). Navigaci na mapě, funkce widgetů a funkci Přejít na naleznete v části [Widget mapy a navigace](02-getting-started.mdx#widget-mapy-a-navigace).

![Celé rozvržení pohledu zákresu JŽ](../imgs/foi-view.png)

## Widget pozorování

**Widget pozorování** zobrazuje dílčí snímky ze satelitů pořízené v průběhu času pro vybraný zákres JŽ.

### Účel

- Zobrazit časovou osu satelitních snímků pro zákres JŽ
- Sledovat změny pokryvu půdy v chronologické posloupnosti
- Identifikovat konkrétní data zájmu pro podrobnější zkoumání

### Možnosti konfigurace

Veškerá nastavení pozorování jsou dostupná prostřednictvím **panelu možností v zápatí pozorování** (ikona ozubeného kola nebo nastavení v dolní části panelu pozorování). Pro tyto ovládací prvky neexistuje samostatný panel Nastavení.

#### Vrstva pozorování

**Výběr vrstvy pozorování**:

1. Otevřete **možnosti zápatí pozorování**
2. Najděte rozbalovací nabídku **Vrstva**
3. Vyberte jednu nebo více vrstev (např. „S2 True Color", „S2 NDVI", „S2 False Color")
4. Pozorování a snímky na mapě se okamžitě aktualizují

**Dostupné vrstvy** jsou specifické pro daný rozsah. Běžné příklady:

- **S2 True Color**: Snímky v přirozených barvách
- **S2 False Color**: Vylepšená vizualizace vegetace
- **S2 NDVI**: Index zdravotního stavu vegetace
- **S2 EVI, LAI, CCC**: Další indexy vegetace
- **Vlastní vrstvy**: Vizualizace specifické pro daný projekt

**Způsoby využití**:

- Přepněte na NDVI pro posouzení zdravotního stavu vegetace
- Použijte True Color pro přirozený vzhled
- Použijte False Color pro zvýraznění hustoty vegetace

#### Časové období

Časové období (datum začátku a konce) je globální nastavení aplikace. Otevřete **boční panel** a upravte **Časové období** tam. Zobrazí se pouze pozorování v tomto rozsahu. Pokud nevidíte žádné snímky, ověřte, zda vaše časové období odpovídá dostupnosti dat.

**Výběr data**:

- Klikněte na pozorování
- Nastaví se časová značka **Vybraného data** v obecném nastavení
- Související widgety (snímky na mapě, widget Signály a indikátory, Časosběrnice) se aktualizují a zobrazí příslušné datum

#### Možnosti rozvržení

- **Obrázků na řádek**: Upravte, kolik pozorování se zobrazí v jednom řádku
  - Méně sloupců = větší obrázky
  - Více sloupců = více viditelných obrázků najednou, ale menších
  - Deaktivováno při zapnutém **Zobrazení podle týdnů**
- **Zobrazení podle týdnů**: Je-li zapnuto, každý řádek představuje jeden kalendářní týden, což usnadňuje rozpoznání týdenních vzorců. Je-li vypnuto, obrázky zaplňují řádky podle nastavení počtu obrázků na řádek.

#### Platné signály

**Přepnutí možnosti Zobrazit pouze platné signály**:

- Zaškrtněte **Zobrazit pouze platné signály**
- Skryje snímky s nekvalitními daty (mraky, stíny, chyby zpracování)
- Odškrtnutí zobrazí všechna dostupná pozorování

**Výběr platných signálů podle zdroje**:

- Pro každý zdroj obrazových dat (kolekci) vyberte typ platného signálu

#### Výplň

- Řídí okraj viditelné oblasti kolem zákresu JŽ
- Větší výplň zobrazuje více kontextu kolem pole
- Menší výplň se zaměřuje těsně na hranici zákresu JŽ

#### Překrytí geometrie

- Je-li zapnuto, hranice zákresu JŽ je vykreslena přes každý dílčí snímek pozorování, takže vidíte, kterou oblast snímek pokrývá
- Vypnutím zobrazíte celý dílčí snímek bez překrytí hranicí

#### Připnuté signály

- V části **Připnuté signály** vyberte typ signálu pro každou kolekci, jehož hodnota se zobrazí na každé kartě pozorování
- Hodnoty se zobrazují jako štítky v záhlaví dílčího snímku
- Pomáhá rychle identifikovat trendy signálu bez přepínání do grafu
- Nastavte na **Žádný** pro vypnutí připnutí pro danou kolekci

![Připnuté signály](../imgs/observations-pinned-signals.png)

### Interakce

**Kliknutím zvětšit**:

1. Klikněte na ikonu rozbalení u libovolného dílčího snímku v panelu pozorování
2. Snímek se otevře v **přetahovatelném a měnitelném vyskakovacím okně**
3. Ve vyskakovacím okně:
   - Přetažením okna změňte jeho polohu
   - Změňte velikost pomocí rohových úchytů
   - Zavřete pomocí tlačítka X
4. Můžete zvětšit více pozorování najednou a porovnat je vedle sebe

![Zvětšení pozorování](../imgs/observations-enlarge.png)

**Navigace v časové ose**:

- Posuňte se panelem pozorování a zobrazte všechna dostupná data
- Snímky jsou řazeny chronologicky

## Widget atributů zákresu JŽ

**Widget atributů zákresu JŽ** zobrazuje obecné referenční informace o vybraném zákresu JŽ.

### Účel

- Zobrazit metadata a charakteristiky zákresu JŽ
- Porozumět vlastnostem pole (plodina, velikost, poloha atd.)
- Přistupovat k referenčním atributům pro analýzu

### Obsah

Zobrazené atributy mohou zahrnovat:

- **ID zákresu JŽ**: Systémem generovaný a jedinečný plně kvalifikovaný identifikátor (ve formátu `<SCOPE>.FOI.<ID>`)
- **Referenční ID**: Referenční ID (ID poskytnutá klientem, jedinečná v rámci rozsahu a typu zákresu JŽ; identifikátor, který uživatelé zpravidla používají pro zákresy JŽ)
- **Typ zákresu JŽ**: Klasifikace zákresu JŽ (pole, parcela atd.)
- **Typ plodiny**: Jaká plodina je deklarována nebo detekována
- **Využití půdy**: Zemědělská kategorie
- **Plocha**: Velikost pole
- **Další atributy**: Metadata specifická pro daný projekt

**Poznámka**: Dostupné atributy závisí na konfiguraci rozsahu a zdrojích dat. Přesná sada zobrazených atributů je definována daty každého rozsahu, takže různé rozsahy zobrazují různé atributy.

## Widget indikátorů

**Widget indikátorů** zobrazuje prediktivní měření ukazující způsoby hospodaření a charakteristiky pole.

Indikátory jsou odvozeny ze vzorců fenologie signálů a poskytují přehled o:

- Předpovědích klasifikace plodin
- Detekci holé půdy
- Událostech sečení
- Analýze homogenity
- A dalších

Widget indikátorů má čtyři záložky: **Souhrn**, **Rozhodnutí**, **Hodnocení** a **Validace** (Validace se zobrazí pouze ve Validační konzoli).

### Záložka Souhrn

Záložka **Souhrn** poskytuje přehled analytických indikátorů na vysoké úrovni. Indikátory rozhodnutí a hodnocení zde **nejsou zobrazeny** — každý má vlastní samostatnou záložku.

![Souhrn indikátorů](../imgs/markers-summary.png)

**Zobrazené informace**:

1. **Klasifikace**:
   - Předpovězená kategorie plodiny (např. „Obiloviny", „Kořenové plodiny")
   - Skóre spolehlivosti zobrazené jako procento
   - Najetím myší na řádek zobrazíte popisky **„Klasifikováno jako"** a **„Deklarováno jako"**
   - Viz [Podrobné zobrazení klasifikace skupiny plodin a využití půdy](#podrobné-zobrazení-klasifikace-skupiny-plodin-a-využití-půdy) a [Podrobné zobrazení indikátorů Vzdálenost a Podobnost](#podrobné-zobrazení-indikátorů-vzdálenost-a-podobnost)

2. **NDVI – průměr**:
   - Průměrná hodnota NDVI za analyzované období
   - Udává celkový zdravotní stav vegetace
   - Číselná hodnota a interpretace

3. **Předpovězená homogenita**:
   - Posouzení uniformity pole pomocí skóre homogenity
   - Skóre spolehlivosti
   - Udává, zda je pole uniformní nebo smíšené

4. **Pozorování** (např. detekce holé půdy):
   - **Počet detekcí** v daném časovém období
   - Data, kdy bylo pozorováno
   - Pomáhá identifikovat události obdělávání

5. **Detekované události** (např. sečení):
   - **Počet identifikovaných událostí** s přímým zobrazením počtů vzestupných a sestupných událostí v řádku souhrnu (např. „2 | 1" pro 2 vzestupné a 1 sestupnou)
   - Data detekovaných událostí

Každý řádek lze rozbalit — kliknutím na řádek zobrazíte úplné podrobnosti.

#### Podrobné zobrazení klasifikace skupiny plodin a využití půdy

Tato dílčí zobrazení ukazují podrobná skóre klasifikace.

**Funkce**:

- **Grafická vizualizace**: Sloupcové grafy zobrazující skóre spolehlivosti pro každou třídu
- **Předpovězená skupina zvýrazněna zeleně**: Předpověď s nejvyšší spolehlivostí
- **Deklarovaná skupina (v případě rozporu) modře**: Zobrazuje, liší-li se deklarovaná plodina od předpovědi
- **Zobrazení všech tříd**: Úplný přehled pravděpodobností klasifikace

**Jak číst výsledky**:

1. Hledejte **zelený sloupec** — to je předpovězená třída
2. Zkontrolujte **procento spolehlivosti** — čím vyšší, tím jistější
3. Je-li přítomen **modrý sloupec**, jde o deklarovanou plodinu (pokud se liší)
4. Porovnejte předpovězenou a deklarovanou hodnotu pro identifikaci možných nesrovnalostí

#### Podrobné zobrazení indikátorů Vzdálenost a Podobnost

Indikátory **Vzdálenost** a **Podobnost** oba porovnávají časovou řadu signálu zákresu JŽ (jeho spektrální profil v průběhu sezóny) s referenčními profily každé kandidátní plodiny, aby posoudily, jak dobře zákres JŽ odpovídá deklarované plodině a které plodině nejvíce odpovídá. Používají **stejné rozvržení sloupcového grafu** jako zobrazení klasifikace — ale měří shodu v opačných směrech, takže jejich grafy jsou **seřazeny opačně**. Každé zobrazení ukazuje **klasifikovanou (nejlépe odpovídající) třídu s jejím skórem** a **deklarovanou třídu s jejím skórem**; nejlépe odpovídající třída je zvýrazněna **zeleně** a **deklarovaná třída je zobrazena modře**, pokud se liší od nejlepší shody.

**Indikátor Vzdálenost**

- **Co měří**: jak spektrálně _odlišný_ je zákres JŽ od referenčního profilu každé třídy (statistická vzdálenost). **Nižší = bližší = více podobný.**
- **Jak číst**: skóre jsou seřazena **od nejnižšího**, takže **horní sloupec je nejbližší (nejlepší) shoda**. Nízké skóre u **deklarované** třídy znamená, že zákres JŽ vypadá jako deklarovaná plodina; vysoké skóre u deklarované třídy znamená, že se od ní liší.

**Indikátor Podobnost**

- **Co měří**: jak těsně spektrální podpis zákresu JŽ _odpovídá_ referenčnímu profilu každé třídy. **Vyšší = více podobný.**
- **Jak číst**: skóre jsou seřazena **od nejvyššího**, takže **horní sloupec je nejlepší shoda**. Pokud deklarovaná třída není blízko vrcholu, profil zákresu JŽ se odchyluje od toho, co je pro danou plodinu očekáváno.

**Interpretace**:

- Silná shoda s plodinou **jinou než deklarovanou** (nízká Vzdálenost nebo vysoká Podobnost k jiné třídě) může naznačovat chybnou klasifikaci nebo nesprávné prohlášení — označte ke kontrole.
- Slabá shoda s **deklarovanou** plodinou (vysoká Vzdálenost nebo nízká Podobnost u deklarované třídy) je signál k podrobnějšímu prověření zákresu JŽ.
- **Častá záměna**: oba indikátory vypadají identicky, ale čtou se opačným způsobem — **nízké skóre Vzdálenosti** znamená dobrou shodu, zatímco **vysoké skóre Podobnosti** znamená dobrou shodu.

---

### Záložka Rozhodnutí

Záložka **Rozhodnutí** zobrazuje všechny indikátory rozhodnutí pro vybraný zákres JŽ. Indikátory rozhodnutí využívají konfigurovatelný rozhodovací strom, který kombinuje více vstupů pro posouzení souladu s obchodními pravidly.

![Indikátory – Rozhodnutí](../imgs/markers-decisions.png)

Indikátory jsou zobrazeny s jejich **čitelným názvem** (nikoli interními kódy). Každý řádek zobrazuje výsledek formou semaforu:

- **Zelená**: Schváleno / Vyhovující
- **Žlutá**: Varování / Vyžaduje pozornost
- **Červená**: Zamítnuto / Nevyhovující

**Diagram rozhodnutí**:

1. Kliknutím na řádek indikátoru rozhodnutí ho rozbalíte
2. Zobrazí se úplný diagram rozhodovacího stromu jako obrázek, který vizualizuje logiku vedoucí k výsledku rozhodnutí
3. Diagram je statický — uzly nemají interaktivní popisky ani stavy při najetí myší

### Záložka Hodnocení

Záložka **Hodnocení** zobrazuje indikátory hodnocení pro vybraný zákres JŽ s odpověďmi ve formátu ano/ne (boolean).

![Indikátory – Hodnocení](../imgs/markers-evaluations.png)

Indikátory jsou zobrazeny s jejich **čitelným názvem**. Řádky nelze rozbalit. Pokud není v řádku dostatek místa pro zobrazení celé otázky hodnocení, je otázka zkrácena pomocí ... a celá otázka se zobrazí v popisku.

## Widget vizualizace signálů a indikátorů

**Widget vizualizace signálů a indikátorů** zobrazuje časový graf signálů a indikátorů pro vybraný zákres JŽ. Které signály a indikátory jsou v tomto widgetu zobrazeny, je definováno v konfiguraci rozsahu (viz [Konfigurace rozsahu](05-scope-configuration.mdx)).

![Widget vizualizace signálů a indikátorů](../imgs/sig-ma-viz.png)

### Účel

- Vizualizovat data signálů a indikátorů v čase
- Identifikovat trendy a vzorce
- Korelovat signály se snímky a indikátory

### Funkce

#### Zobrazení signálů a indikátorů

- Dostupné a nakonfigurované signály a indikátory pro vybraný zákres JŽ a kontext indikátorů
- Signály zobrazeny jako spojité čáry
- Indikátory zobrazeny jako body nebo diskrétní ukazatele
- Barevně odlišeno pro snadnou identifikaci
- **Referenční data zákresu JŽ** (datumové atributy nakonfigurované pro každý rozsah) jsou zobrazena jako **statické svislé páskové indikátory** v grafu

#### Interaktivní legenda a ovládací prvky

Panel ovládacích prvků je připojen k boku grafu (výchozí poloha: vpravo). Obsahuje:

- **Barevná zaškrtávací políčka**: Přepínání viditelnosti jednotlivých signálů nebo indikátorů v grafu
- **Popisek**: Přepnutím zobrazení signálu/indikátoru v datovém popisku kliknutím na jeho název — **tučné** názvy označují, že signál/indikátor bude zobrazen v popisku
- **Nabídka Možnosti**: Úprava umístění popisku a přesun panelu ovládacích prvků na jinou pozici (vlevo, vpravo, nahoře, dole)

#### Možnosti umístění popisku

- **Kurzor**: Popisek sleduje myš
- **Horní, Dolní, Vlevo, Vpravo**: Pevná poloha vůči grafu
- **Rohy**: Vlevo nahoře, vpravo nahoře, vlevo dole, vpravo dole

#### Výběr data

- Klikněte na datum v grafu
- Nastaví se časová značka **Vybraného data** v obecném nastavení
- Související widgety (snímky na mapě, pozorování, Časosběrnice) se aktualizují a zobrazí příslušné datum
- Korelujte signály s prostorovými snímky

#### Podpora více os (je-li povolena)

- Signálům lze přiřadit různé osy Y
- Až 3 osy (levá, pravá nebo obě)
- Umožňuje porovnání signálů s různými rozsahy hodnot
- Popisky a měřítka os jsou zobrazeny na okrajích grafu

## Widget časosběrnice

**Widget časosběrnice** vytváří animovanou vizualizaci pozorování zákresu JŽ v průběhu času.

### Účel

- Zobrazit dynamické změny v čase formou animace
- Vizuálně identifikovat časové vzorce
- Vytvářet sdílitelné animace GIF pro sestavy

![Widget časosběrnice](../imgs/timelapse.png)

### Ovládací prvky

#### Přehrání animace

1. Klikněte na tlačítko **Přehrát**
2. Pozorování se přehrávají v chronologické posloupnosti
3. Animace se opakuje ve smyčce nebo se zastaví na konci (závisí na nastavení)
4. Kliknutím na **Pauza** zastavíte přehrávání

#### Procházení dílčích snímků

- Pomocí tlačítek **Přejít na předchozí snímek** a **Přejít na další snímek** ručně procházejte pozorování
- Posun o jeden snímek
- Vraťte se zpět a zkontrolujte konkrétní data

#### Rychlost přehrávání

- Upravte rychlost animace
- Pomalá pro detailní zkoumání
- Rychlá pro celkový přehled

#### Výběr vrstvy

- Zvolte, kterou vrstvu pozorování animovat (stejná jako ve widgetu pozorování)
- Časosběrnice odráží aktuálně vybranou vrstvu
- Přepínejte, které geometrie zobrazit (původní hranice zákresu JŽ, pixelovaný zákres JŽ)

#### Stažení animace jako GIF

1. Klikněte na tlačítko **Exportovat GIF**
2. Časosběrnice je vykreslena a uložena jako soubor GIF
3. GIF použijte v prezentacích, sestavách nebo externích nástrojích
